import React, { useEffect, useState, useCallback } from 'react';
import { FaHandshake, FaTrash, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import paymentPromiseService from '../services/paymentPromiseService';

const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(n || 0);
const toStr = (d) => (d ? String(d).split('T')[0] : '');

const STATUS_LABELS = { bekliyor: 'Bekliyor', odendi: 'Ödendi', gecikti: 'Gecikti' };
const STATUS_COLORS = { bekliyor: 'warning', odendi: 'success', gecikti: 'danger' };

// ---------- Mini Takvim ----------
const MiniCalendar = ({ promises, onDayClick, selectedDay }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().split('T')[0];

  // Map: 'YYYY-MM-DD' → [promise, ...]
  const dayMap = {};
  promises.forEach(p => {
    const d = toStr(p.promise_date);
    if (!dayMap[d]) dayMap[d] = [];
    dayMap[d].push(p);
  });

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const monthName = new Date(year, month, 1).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  const blanks = firstDay === 0 ? 6 : firstDay - 1; // Monday start

  const getDotColor = (ps) => {
    if (ps.some(p => p.status === 'gecikti')) return '#dc3545';
    if (ps.some(p => p.status === 'bekliyor')) return '#ffc107';
    return '#198754';
  };

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <button className="btn btn-sm btn-outline-secondary" onClick={prevMonth}><FaChevronLeft /></button>
        <span className="fw-semibold text-capitalize">{monthName}</span>
        <button className="btn btn-sm btn-outline-secondary" onClick={nextMonth}><FaChevronRight /></button>
      </div>
      <div className="card-body p-2">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {['Pt','Sa','Ça','Pe','Cu','Ct','Pz'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6c757d', padding: '4px 0' }}>{d}</div>
          ))}
          {Array(blanks).fill(null).map((_, i) => <div key={`b${i}`} />)}
          {Array(daysInMonth).fill(null).map((_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const ps = dayMap[dateStr] || [];
            const isToday = dateStr === today;
            const isSelected = dateStr === selectedDay;
            const hasProm = ps.length > 0;
            return (
              <div
                key={day}
                onClick={() => hasProm && onDayClick(dateStr)}
                style={{
                  textAlign: 'center', padding: '6px 2px', borderRadius: 6, cursor: hasProm ? 'pointer' : 'default',
                  background: isSelected ? '#c0392b' : isToday ? '#f8f9fa' : 'transparent',
                  border: isToday ? '1px solid #dee2e6' : '1px solid transparent',
                  color: isSelected ? '#fff' : '#212529',
                  fontSize: 13, position: 'relative', fontWeight: hasProm ? 700 : 400
                }}
              >
                {day}
                {hasProm && (
                  <div style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 2, justifyContent: 'center' }}>
                    {ps.slice(0, 3).map((p, pi) => (
                      <div key={pi} style={{ width: 5, height: 5, borderRadius: '50%', background: getDotColor([p]) }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="card-footer bg-white border-0 d-flex gap-3 justify-content-center pb-2" style={{ fontSize: 11 }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ffc107', marginRight: 4 }} />Bekliyor</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#dc3545', marginRight: 4 }} />Gecikti</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#198754', marginRight: 4 }} />Ödendi</span>
      </div>
    </div>
  );
};

// ---------- Ana Sayfa ----------
const PaymentPromisesPage = () => {
  const [promises, setPromises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedDay, setSelectedDay] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await paymentPromiseService.getAll();
      setPromises(data);
    } catch {
      toast.error('Ödeme sözleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleMarkPaid = async (id) => {
    try {
      await paymentPromiseService.updateStatus(id, 'odendi');
      toast.success('Ödendi olarak işaretlendi.');
      fetch();
    } catch { toast.error('Güncelleme başarısız.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ödeme sözünü silmek istiyor musunuz?')) return;
    try {
      await paymentPromiseService.delete(id);
      toast.success('Silindi.');
      fetch();
    } catch { toast.error('Silme başarısız.'); }
  };

  const today = new Date().toISOString().split('T')[0];

  const filtered = promises.filter(p => {
    const d = toStr(p.promise_date);
    if (selectedDay) return d === selectedDay;
    if (filter === 'all') return true;
    if (filter === 'bekliyor') return p.status === 'bekliyor';
    if (filter === 'gecikti') return p.status === 'gecikti';
    if (filter === 'odendi') return p.status === 'odendi';
    if (filter === 'today') return d === today;
    return true;
  });

  const counts = {
    all: promises.length,
    bekliyor: promises.filter(p => p.status === 'bekliyor').length,
    gecikti: promises.filter(p => p.status === 'gecikti').length,
    odendi: promises.filter(p => p.status === 'odendi').length,
    today: promises.filter(p => toStr(p.promise_date) === today).length,
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-secondary mb-0"><FaHandshake className="me-2" />Ödeme Sözleri</h2>
        {selectedDay && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedDay(null)}>
            Tüm günleri göster
          </button>
        )}
      </div>

      <div className="row">
        {/* Takvim */}
        <div className="col-lg-4 col-xl-3">
          <MiniCalendar
            promises={promises}
            selectedDay={selectedDay}
            onDayClick={(d) => setSelectedDay(prev => prev === d ? null : d)}
          />
        </div>

        {/* Liste */}
        <div className="col-lg-8 col-xl-9">
          {/* Filtre sekmeler */}
          {!selectedDay && (
            <div className="d-flex gap-2 flex-wrap mb-3">
              {[
                { key: 'all', label: 'Tümü' },
                { key: 'today', label: 'Bugün' },
                { key: 'bekliyor', label: 'Bekliyor' },
                { key: 'gecikti', label: 'Gecikti' },
                { key: 'odendi', label: 'Ödendi' },
              ].map(f => (
                <button
                  key={f.key}
                  className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label} <span className="badge bg-white text-dark ms-1">{counts[f.key]}</span>
                </button>
              ))}
            </div>
          )}

          {selectedDay && (
            <div className="alert alert-info py-2 mb-3">
              <strong>{new Date(selectedDay + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong> tarihindeki ödeme sözleri gösteriliyor.
            </div>
          )}

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaHandshake size={40} className="mb-3 opacity-25" />
              <p>Bu filtre için ödeme sözü bulunamadı.</p>
            </div>
          ) : (
            <div className="card shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Tarih</th>
                      <th>Müşteri</th>
                      <th className="text-end">Tutar</th>
                      <th>Durum</th>
                      <th>Not</th>
                      <th className="text-center">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => {
                      const d = toStr(p.promise_date);
                      const isToday = d === today;
                      const isOverdue = p.status === 'gecikti';
                      return (
                        <tr key={p.id} className={isOverdue ? 'table-danger' : isToday ? 'table-warning' : ''}>
                          <td className="fw-semibold">
                            {new Date(d + 'T12:00:00').toLocaleDateString('tr-TR')}
                            {isToday && <span className="badge bg-warning text-dark ms-2">Bugün</span>}
                            {isOverdue && <span className="badge bg-danger ms-2">Gecikti</span>}
                          </td>
                          <td>{p.company_name}</td>
                          <td className="text-end fw-bold">₺{fmt(p.amount)}</td>
                          <td>
                            <span className={`badge bg-${STATUS_COLORS[p.status]}`}>
                              {STATUS_LABELS[p.status]}
                            </span>
                          </td>
                          <td className="text-muted small">{p.notes || '-'}</td>
                          <td className="text-center">
                            {p.status !== 'odendi' && (
                              <button
                                className="btn btn-sm btn-success me-1"
                                title="Ödendi işaretle"
                                onClick={() => handleMarkPaid(p.id)}
                              >
                                <FaCheck />
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Sil"
                              onClick={() => handleDelete(p.id)}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPromisesPage;
